import React, { FC, useEffect, useState } from 'react';
import { t } from 'i18next';
import {
  TableHeaderCont,
  ReportColumn,
  AccessColumn,
  ResponseRateColumn,
  TableBody,
  TableFooter,
  ResultsSearchBox,
} from './ShareResults.styles';
import ShareResultsTile from './ShareResultsTile/ShareResultsTile';
import GoToPage from '../../components/GoToPage/GoToPage';
import NoData from '../../components/NoData/NoData';
import Pagination from '../../components/Pagination/Pagination';
import { Header, InfoDetail, InfoTitle } from '../../components/ReportsSetup/ReportsSetup.styles';
import RowsPerPage from '../../components/RowsPerPage/RowsPerPage';
import SearchBox from '../../components/SearchBox/SearchBox';
import ShareResultsModal from '../../components/ShareResultsModal/ShareResultsModal';
import { useFilters } from '../../hooks/useFilters';
import { useReports } from '../../hooks/useReports';
import { IItemReportResponse } from '../../services/reports';
import { IPagingInfo } from '../../services/surveys';

const defaultPagingInfo: IPagingInfo = {
  pageNo: 1,
  itemsPerPage: 7,
  totalPageCount: 1,
  totalItemsCount: 1,
  memberCount: 1,
  subordinateCount: 1,
  superordinateCount: 1,
  leaderCount: 1,
  teamReportsCount: null,
  executiveReportsCount: null,
};

interface ShareResultsProps {
  id: string;
  surveyType: string;
  shareResultsItems: IItemReportResponse[];
  setShareResultsItems: (items: IItemReportResponse[]) => void;
}

const ShareResults: FC<ShareResultsProps> = ({ id, surveyType, shareResultsItems, setShareResultsItems }) => {
  const { downloadReport } = useReports();

  const [metadata, setMetadata] = useState<IPagingInfo>(defaultPagingInfo);

  const { applyFilters, currentFilters } = useFilters();

  const { card, perPage, pageNo, search } = currentFilters;

  const toFilterString = (value: string | (string | null)[] | null | undefined) => {
    if (Array.isArray(value)) {
      return value.find((entry): entry is string => typeof entry === 'string') ?? null;
    }
    return typeof value === 'string' ? value : null;
  };

  const params = {
    pageNo: toFilterString(pageNo),
    perPage: toFilterString(perPage),
    search: toFilterString(search),
  };

  const processReports = async () => {
    if (id) {
      const reportsResponse = await downloadReport(id, params, true);
      const { items: downloadedItems, pagingInfo } = reportsResponse;
      setMetadata(pagingInfo);
      setShareResultsItems(downloadedItems);
    }
  };

  const searchReports = (search: Record<string, string | null | undefined>) => {
    applyFilters(search);
  };

  useEffect(() => {
    (async () => await processReports())();
  }, [card, pageNo, perPage, id, search]);

  const [selectedReport, setSelectedReport] = useState<string | null>(null);

  const findReportById = (
    reports: IItemReportResponse[],
    reportId: string | null
  ): IItemReportResponse | undefined => {
    if (!reportId) return undefined;

    for (const report of reports) {
      if (report.id === reportId) return report;
      if (report.sub_reports?.length) {
        const nestedReport = findReportById(report.sub_reports, reportId);
        if (nestedReport) return nestedReport;
      }
    }

    return undefined;
  };

  const reportObj = findReportById(shareResultsItems, selectedReport);

  const { totalPageCount, itemsPerPage, totalItemsCount } = metadata;

  return (
    <>
      {selectedReport && (
        <ShareResultsModal
          selectedReport={selectedReport}
          closeModal={() => setSelectedReport(null)}
          title={reportObj?.group || ''}
          recipients={reportObj?.accessList?.users || []}
          departmentId={reportObj?.department || 1}
          processReports={processReports}
          surveyId={id}
          surveyType={surveyType}
        />
      )}
      <Header id="Header">
        <InfoTitle>
          {t('viewSurvey.sharing.titles.allReports')}:<InfoDetail>{totalItemsCount}</InfoDetail>
        </InfoTitle>
      </Header>
      <ResultsSearchBox>
        <SearchBox
          searchPhrase={searchReports}
          placeholder={t('viewSurvey.participants.listHead.search')}
          isIcon={true}
          filterName="search"
          isButtonAvailable={false}
        />
      </ResultsSearchBox>
      <TableHeaderCont id="TableHeaderCont">
        <ReportColumn>{t('viewSurvey.sharing.tableHeader.report')}</ReportColumn>
        <AccessColumn>{t('viewSurvey.sharing.tableHeader.access')}</AccessColumn>
        <ResponseRateColumn>{t('viewSurvey.sharing.tableHeader.responseRate')}</ResponseRateColumn>
      </TableHeaderCont>
      <TableBody>
        {shareResultsItems?.length ? (
          shareResultsItems.map((item, index) => (
            <ShareResultsTile
              key={item.id}
              item={item}
              processReports={processReports}
              setSelectedReport={setSelectedReport}
              depth={0}
              isLastChild={index === shareResultsItems.length - 1}
            />
          ))
        ) : (
          <NoData message={t('viewSurvey.sharing.tableBody.noReportsInfo')} />
        )}
      </TableBody>
      <TableFooter id="TableFooter">
        <RowsPerPage
          itemsPerPage={Number(itemsPerPage)}
          pagesCount={Number(totalPageCount)}
          menu={['7', '10', '25', '50', '75', '100']}
        />
        <Pagination
          activePage={Number(pageNo || 1)}
          totalPageCount={Number(totalPageCount)}
          applyFilters={pageNo => applyFilters({ pageNo: String(pageNo) })}
        />
        <GoToPage applyFilters={applyFilters} pageLimit={totalPageCount} />
      </TableFooter>
    </>
  );
};

export default ShareResults;
